import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import PersonForm from './PersonForm';
import { Person, CreatePerson, UpdatePerson } from '../types';
import { personsAPI } from '../services/api';
import { navigateWithRedirect } from '../utils/redirectUtils';

const PersonEditPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const [person, setPerson] = useState<Person | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestedCustomerId = Number(searchParams.get('customerId'));
  const initialCustomerId = !id && Number.isSafeInteger(requestedCustomerId) && requestedCustomerId > 0
    ? requestedCustomerId
    : undefined;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch person if editing
        if (id) {
          const personData = await personsAPI.getPerson(parseInt(id));
          setPerson(personData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load person');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (personData: CreatePerson | UpdatePerson) => {
    setIsLoading(true);
    setError(null);
    try {
      if ('id' in personData) {
        // Update existing person
        await personsAPI.updatePerson(personData.id, personData);
      } else {
        // Create new person
        await personsAPI.createPerson(personData);
      }
      // Navigate back to redirect URL or default to persons list
      navigateWithRedirect(navigate, '/persons', searchParams);
    } catch (error: any) {
      setError(error?.response?.data?.message || 'Failed to save person');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to redirect URL or default to persons list
    navigateWithRedirect(navigate, '/persons', searchParams);
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="spinner-border" role="status"></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="row">
          <div className="col-12">
            <PersonForm
              key={`${id || 'new'}-${initialCustomerId || ''}`}
              person={person}
              initialCustomerId={initialCustomerId}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonEditPage;
